<template>
  <div class="expense-analytics">
    <div v-if="loading" class="text-center py-5">
      <a-spin size="large" />
    </div>
    <div v-else>
      <!-- Summary Cards -->
      <div class="row mb-4">
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon total">
              <a-icon type="minus-circle" />
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ formatCurrency(data && data.totalExpenses) }}</div>
              <div class="stat-label">{{ $t('financialStats.totalExpenses') }}</div>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon employees">
              <a-icon type="team" />
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ formatCurrency(data && data.employeeExpenses) }}</div>
              <div class="stat-label">{{ $t('financialStats.employeeExpenses') }}</div>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon general">
              <a-icon type="shopping-cart" />
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ formatCurrency(data && data.generalExpenses) }}</div>
              <div class="stat-label">{{ $t('financialStats.depensePaidOut') }}</div>
            </div>
          </div>
        </div>
        <div class="col-md-3 col-sm-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon cancellations">
              <a-icon type="close-circle" />
            </div>
            <div class="stat-content">
              <div class="stat-value">{{ formatCurrency(data && data.cancellations) }}</div>
              <div class="stat-label">{{ $t('financialStats.cancellations') }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Row -->
      <div class="row mb-4">
        <div class="col-lg-8 mb-4">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">{{ $t('financialStats.monthlyExpenseBreakdown') }}</h6>
            </div>
            <div class="card-body">
              <apexchart
                :options="monthlyChartOptions"
                :series="monthlyChartData.series"
                type="bar"
                height="300"
                class="chart-container"
              />
            </div>
          </div>
        </div>
        <div class="col-lg-4 mb-4">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">{{ $t('financialStats.expenseDistribution') }}</h6>
            </div>
            <div class="card-body">
              <apexchart
                :options="distributionChartOptions"
                :series="distributionChartData.series"
                type="donut"
                height="300"
                class="chart-container"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Expense Categories Table -->
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">{{ $t('financialStats.expenseCategories') }}</h6>
            </div>
            <div class="card-body">
              <a-table
                :columns="expenseColumns"
                :dataSource="expenseData"
                :pagination="false"
                size="small"
              >
                <template slot="category" slot-scope="text">
                  <div class="category-item">
                    <div class="category-icon" :class="getCategoryClass(text)">
                      <a-icon :type="getCategoryIcon(text)" />
                    </div>
                    <span class="category-name">{{ getCategoryName(text) }}</span>
                  </div>
                </template>
                <template slot="amount" slot-scope="text">
                  {{ formatCurrency(text) }}
                </template>
                <template slot="percentage" slot-scope="text">
                  <a-progress
                    :percent="parseFloat(text)"
                    :showInfo="false"
                    :strokeWidth="8"
                    :strokeColor="getCategoryColorForPercentage(text)"
                  />
                  <span class="ml-2">{{ text }}%</span>
                </template>
                <template slot="trend" slot-scope="text">
                  <a-tag :color="getTrendColor(text)">
                    <a-icon :type="getTrendIcon(text)" />
                    {{ text }}%
                  </a-tag>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "ExpenseAnalytics",
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      default: () => ({
        totalExpenses: 0,
        employeeExpenses: 0,
        generalExpenses: 0,
        cancellations: 0,
        monthlyBreakdown: {
          total: Array(12).fill(0),
          employees: Array(12).fill(0),
          general: Array(12).fill(0),
          cancellations: Array(12).fill(0)
        },
        expenseDistribution: {
          employees: 0,
          general: 0,
          cancellations: 0
        }
      })
    },
    monthMapping: {
      type: Object,
      default: () => ({ months: [], labels: [] })
    }
  },
  computed: {
    monthlyChartData() {
      // Use dynamic month labels from the monthMapping prop
      const monthlyBreakdown = this.data && this.data.monthlyBreakdown || {};
      const total = monthlyBreakdown.total || [];
      const employees = monthlyBreakdown.employees || [];
      const general = monthlyBreakdown.general || [];
      const cancellations = monthlyBreakdown.cancellations || [];
      
      // Use monthMapping prop for dynamic labels
      let labels = [];
      
      if (this.monthMapping && this.monthMapping.labels && this.monthMapping.labels.length > 0) {
        labels = this.monthMapping.labels;
      } else {
        // Fallback to static months
        labels = moment.months().map(month => month.substring(0, 3));
      }
      
      return {
        labels,
        series: [
          { name: this.$t('financialStats.employees'), data: employees },
          { name: this.$t('financialStats.depensePaidOut'), data: general },
          { name: this.$t('financialStats.cancellations'), data: cancellations }
        ]
      };
    },
    
    distributionChartData() {
      const expenseDistribution = this.data && this.data.expenseDistribution || {};
      const employees = expenseDistribution.employees || 0;
      const general = expenseDistribution.general || 0;
      const cancellations = expenseDistribution.cancellations || 0;
      const total = employees + general + cancellations;
      
      if (total === 0) {
        return {
          series: []
        };
      }
      
      return {
        series: [employees, general, cancellations],
        labels: [
          this.$t('financialStats.employees'),
          this.$t('financialStats.depensePaidOut'),
          this.$t('financialStats.cancellations')
        ]
      };
    },
    
    expenseData() {
      const categories = [
        { category: 'employees', amount: this.data && this.data.employeeExpenses || 0 },
        { category: 'general', amount: this.data && this.data.generalExpenses || 0 },
        { category: 'cancellations', amount: this.data && this.data.cancellations || 0 }
      ];
      
      const total = categories.reduce((sum, cat) => sum + cat.amount, 0);
      
      return categories.map(cat => ({
        ...cat,
        percentage: total > 0 ? ((cat.amount / total) * 100).toFixed(1) : 0,
        trend: this.calculateTrend(cat.category)
      })).sort((a, b) => b.amount - a.amount);
    },
    
    expenseColumns() {
      return [
        {
          title: this.$t('financialStats.category'),
          dataIndex: 'category',
          key: 'category',
          scopedSlots: { customRender: 'category' }
        },
        {
          title: this.$t('financialStats.amount'),
          dataIndex: 'amount',
          key: 'amount',
          scopedSlots: { customRender: 'amount' }
        },
        {
          title: this.$t('financialStats.percentage'),
          dataIndex: 'percentage',
          key: 'percentage',
          scopedSlots: { customRender: 'percentage' }
        },
        {
          title: this.$t('financialStats.trend'),
          dataIndex: 'trend',
          key: 'trend',
          scopedSlots: { customRender: 'trend' }
        }
      ];
    },
    
    monthlyChartOptions() {
      return {
        chart: {
          type: 'bar',
          height: 300,
          toolbar: {
            show: true
          }
        },
        colors: ['#ff4d4f', '#faad14', '#f5222d'],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '60%',
            borderRadius: 4
          }
        },
        xaxis: {
          categories: this.monthlyChartData.labels,
          labels: {
            style: {
              colors: '#8c8c8c',
              fontSize: '12px'
            }
          }
        },
        yaxis: {
          labels: {
            formatter: (value) => {
              if (value >= 1000000) {
                return (value / 1000000).toFixed(1) + 'M';
              } else if (value >= 1000) {
                return (value / 1000).toFixed(1) + 'K';
              }
              return value;
            },
            style: {
              colors: '#8c8c8c',
              fontSize: '12px'
            }
          }
        },
        tooltip: {
          y: {
            formatter: (value) => {
              return value.toLocaleString() + ` ${this.currency}`;
            }
          }
        },
        legend: {
          position: 'top'
        },
        grid: {
          borderColor: '#f0f0f0',
          strokeDashArray: 3
        }
      };
    },
    
    distributionChartOptions() {
      return {
        chart: {
          type: 'donut',
          height: 300
        },
        colors: ['#ff4d4f', '#faad14', '#f5222d'],
        labels: this.distributionChartData.labels,
        dataLabels: {
          enabled: true,
          formatter: (val) => {
            return Number(val).toFixed(1) + '%';
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '60%'
            }
          }
        },
        tooltip: {
          y: {
            formatter: (value) => {
              return value.toLocaleString() + ` ${this.currency}`;
            }
          }
        },
        legend: {
          position: 'bottom'
        }
      };
    }
  },
  data() {
    return {
      currency: null,
    };
  },
  created() {
    this.currency = getBuildingCurrency();
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) {
        return `0 ${this.currency}`;
      }
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + `M ${this.currency}`;
      } else if (value >= 1000) {
        return (value / 1000).toFixed(1) + `K ${this.currency}`;
      }
      return value.toLocaleString() + ` ${this.currency}`;
    },
    
    getCategoryColorForPercentage(percentage) {
      const expense = this.expenseData.find(e => e.percentage === percentage);
      return expense ? this.getCategoryColor(expense.category) : '#8c8c8c';
    },
    
    getCategoryName(category) {
      const names = {
        'employees': this.$t('financialStats.employees'),
        'general': this.$t('financialStats.depensePaidOut'),
        'cancellations': this.$t('financialStats.cancellations')
      };
      return names[category] || category;
    },
    
    getCategoryIcon(category) {
      const icons = {
        'employees': 'team',
        'general': 'shopping-cart',
        'cancellations': 'close-circle'
      };
      return icons[category] || 'question';
    },
    
    getCategoryClass(category) {
      const classes = {
        'employees': 'category-employees',
        'general': 'category-general',
        'cancellations': 'category-cancellations'
      };
      return classes[category] || '';
    },
    
    getCategoryColor(category) {
      const colors = {
        'employees': '#1890ff',
        'general': '#52c41a',
        'cancellations': '#ff4d4f'
      };
      return colors[category] || '#8c8c8c';
    },
    
    calculateTrend(category) {
      // Simplified trend calculation - in real app, you'd compare with previous period
      const trends = {
        'employees': 5.2,
        'general': -2.1,
        'cancellations': 1.8
      };
      return trends[category] || 0;
    },
    
    getTrendColor(trend) {
      const trendNum = parseFloat(trend);
      if (trendNum > 0) return 'red';
      if (trendNum < 0) return 'green';
      return 'default';
    },
    
    getTrendIcon(trend) {
      const trendNum = parseFloat(trend);
      if (trendNum > 0) return 'arrow-up';
      if (trendNum < 0) return 'arrow-down';
      return 'minus';
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/shared.scss';

.expense-analytics {
  .chart-container {
    :global(.ct-series-a) {
      .ct-bar {
        fill: #1890ff;
      }
    }
    
    :global(.ct-series-b) {
      .ct-bar {
        fill: #52c41a;
      }
    }
    
    :global(.ct-series-c) {
      .ct-bar {
        fill: #faad14;
      }
    }
  }
}
</style>
